import {
  NavigationConfiguration,
  NavigationContext,
  NavigationItem,
  NavigationItemType,
  NavigationItemTypes
} from './types';
import { normalizePath } from './utils';

/**
 * Traverses the navigation tree and returns a context matching the provided active path
 */
export function getNavigationContextByActivePath(
  navConfig: NavigationConfiguration,
  activePath: string
): NavigationContext | undefined {
  for (const item of navConfig) {
    if (Array.isArray(item)) {
      const [config] = item;

      if (typeof config !== 'string' && 'root' in config) {
        const normalizedRoot = normalizePath(config.root);

        const doesRootMatchActivePath =
          activePath === normalizedRoot ||
          activePath.startsWith(normalizedRoot + '/');

        if (doesRootMatchActivePath) {
          return item as NavigationContext;
        }
      }
    }
  }
}

/**
 * Given a navigation item, determines its type based on the structure and returns a constant string representation of the type
 */
export function determineNavigationItemType(
  item: NavigationItem
): NavigationItemType | undefined {
  if (item === '---') {
    return 'separator';
  }

  if (
    (typeof item === 'string' && item.startsWith('# ')) ||
    (typeof item === 'object' && 'title' in item && item.title.startsWith('# '))
  ) {
    return 'heading';
  }

  if (
    typeof item === 'string' ||
    (!Array.isArray(item) && typeof item === 'object')
  ) {
    return 'text';
  }

  const [, rest] = item;

  if (typeof rest === 'string') {
    return 'link';
  }

  if (Array.isArray(rest)) {
    return 'section';
  }

  return undefined;
}

/**
 * Validate that a navigation item is of the specified type
 */
export function isNavigationItemOfType<ExpectedType extends NavigationItemType>(
  item: unknown,
  type: ExpectedType
): item is NavigationItemTypes[ExpectedType] {
  return determineNavigationItemType(item as NavigationItem) === type;
}
